import React, { useEffect, useState, useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Container,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { blogs } from "../data/blogs";
import { blogArticles } from "../data/blogArticles";
import { formatDate, slugify } from "../utils/helpers";
import { generateBlogSEO, updateMetaTags } from "../utils/seo";
import Footer from "../components/Footer";
import ShareButtons from "../components/ShareButtons";
import ParticleHero from "../components/ParticleHero";

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const blog = slug ? blogs.find((b) => slugify(b.title) === slug) : undefined;
  const article = blog ? blogArticles[blog.id] : undefined;
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: headerRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.8], [0.45, 0.15]);

  const shareUrl = blog
    ? `https://www.shashi-sharma.com.np/blog/${slugify(blog.title)}`
    : "";

  useEffect(() => {
    const loadMarkdown = async () => {
      if (!article) return;

      try {
        setLoading(true);
        if (article.markdownFilePath) {
          // Load from file
          const response = await fetch(article.markdownFilePath);
          const text = await response.text();
          setMarkdownContent(text);
        } else if (article.markdownContent) {
          // Use inline content (fallback)
          setMarkdownContent(article.markdownContent);
        }
      } catch (error) {
        console.error("Error loading markdown:", error);
        setMarkdownContent(
          "# Error loading article\n\nPlease try again later.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, [article]);

  useEffect(() => {
    if (blog && markdownContent) {
      const seo = generateBlogSEO(blog, markdownContent);
      updateMetaTags(seo);
    }

    // Scroll to top when blog changes
    window.scrollTo(0, 0);
  }, [blog, markdownContent]);

  if (!blog || !article) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box component="article" sx={{ flex: 1 }}>
        <Box
          component="header"
          ref={headerRef}
          sx={{
            position: "relative",
            height: { xs: 340, md: 460 },
            overflow: "hidden",
            mb: 4,
          }}
        >
          {/* Parallax cover image */}
          <motion.div
            style={{
              y: imgY,
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "140%",
              top: "-20%",
            }}
          >
            <Box
              component="img"
              src={blog.coverImage}
              alt={blog.title}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </motion.div>

          {/* Three.js particle field overlay */}
          <motion.div
            style={{
              opacity: overlayOpacity,
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          >
            <ParticleHero height="100%" />
          </motion.div>

          {/* Dark gradient + title */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.55) 55%, transparent 100%)",
              py: 4,
            }}
          >
            <Container maxWidth="md">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 2,
                    // CSS 3D extruded text
                    textShadow: `
                      1px 1px 0 #91bd3a,
                      2px 2px 0 #82aa33,
                      3px 3px 0 #71962b,
                      4px 4px 0 #5f8022,
                      5px 5px 0 #4d6b19,
                      6px 6px 12px rgba(0,0,0,0.7)
                    `,
                  }}
                >
                  {blog.title}
                </Typography>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 2,
                    alignItems: "center",
                    color: "rgba(255,255,255,0.9)",
                  }}
                >
                  <Typography variant="body2">{blog.author}</Typography>
                  <Typography variant="body2">•</Typography>
                  <Typography variant="body2">
                    {formatDate(blog.publishedAt)}
                  </Typography>
                  <Typography variant="body2">•</Typography>
                  <Typography variant="body2">
                    {blog.readTimeMinutes} min read
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {blog.tags.map((tag, i) => (
                    <motion.div
                      key={tag}
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.4 + i * 0.07 }}
                    >
                      <Chip
                        label={tag}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(145, 189, 58, 0.9)",
                          color: "white",
                          fontWeight: 500,
                        }}
                      />
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Container>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            px: { xs: 2, lg: 3 },
            pb: 6,
          }}
        >
          <Container maxWidth="md" sx={{ width: "100%", px: { xs: 0, sm: 2 } }}>
            <ShareButtons
              url={shareUrl}
              title={blog.title}
              description={blog.excerpt}
            />
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  "& h1": {
                    fontSize: "2rem",
                    fontWeight: 700,
                    mt: 4,
                    mb: 2,
                    color: "text.primary",
                  },
                  "& h2": {
                    fontSize: "1.75rem",
                    fontWeight: 600,
                    mt: 3.5,
                    mb: 1.5,
                    color: "text.primary",
                  },
                  "& h3": {
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    mt: 3,
                    mb: 1.5,
                    color: "text.primary",
                  },
                  "& p": {
                    lineHeight: 1.8,
                    mb: 2,
                    color: "text.secondary",
                  },
                  "& a": {
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                  "& code": {
                    backgroundColor: "rgba(145, 189, 58, 0.1)",
                    color: "primary.main",
                    padding: "2px 6px",
                    borderRadius: 1,
                    fontSize: "0.875em",
                    fontFamily: "monospace",
                  },
                  "& pre": {
                    backgroundColor: "#2d2d2d",
                    padding: 2,
                    borderRadius: 2,
                    overflow: "auto",
                    mb: 2,
                    "& code": {
                      backgroundColor: "transparent",
                      color: "#f8f8f2",
                      padding: 0,
                    },
                  },
                  "& ul, & ol": {
                    pl: 3,
                    mb: 2,
                    "& li": {
                      mb: 0.5,
                      lineHeight: 1.8,
                      color: "text.secondary",
                    },
                  },
                  "& blockquote": {
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                    pl: 2,
                    py: 1,
                    my: 2,
                    fontStyle: "italic",
                    backgroundColor: "rgba(145, 189, 58, 0.05)",
                    "& p": {
                      mb: 0,
                    },
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 2,
                    my: 3,
                  },
                  "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    mb: 2,
                    "& th, & td": {
                      border: "1px solid",
                      borderColor: "divider",
                      padding: 1,
                      textAlign: "left",
                    },
                    "& th": {
                      backgroundColor: "background.paper",
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {markdownContent}
                </ReactMarkdown>
              </Box>
            )}
          </Container>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default BlogDetail;
